import { HABIT_ACTIVE_MAX, HABIT_ACTIVE_MIN, HABIT_CUSTOM_MAX, PILAR, PILAR_LABEL, type Pilar } from '@iqrapath/shared'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Settings2, Trash2 } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import { Sheet } from '../../components/Sheet.tsx'
import { ApiError } from '../../lib/api.ts'
import { sparkBurst } from '../../lib/motion.ts'
import { addHabit, checkHabit, dayShort, refreshPerjalanan, removeHabit, setActiveHabits, useHabits, type Garden, type HabitDay } from './perjalanan.ts'

const msg = (e: unknown) => (e instanceof ApiError && e.status !== 0 ? e.message : 'Belum tersimpan. Cek koneksi, lalu coba lagi.')

/** Choose 3–5 active habits, add or remove your own (docs/PRD.md §6.12). */
function HabitSheet({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { data } = useHabits()
  const [active, setActive] = useState<string[] | null>(null)
  const [label, setLabel] = useState('')
  const [pilar, setPilar] = useState<Pilar>('amal')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)
  useEffect(() => { if (data && active === null) setActive(data.habits.map((h) => h.key)) }, [data, active])

  const sel = active ?? []
  const toggle = (k: string) => setActive(sel.includes(k) ? sel.filter((x) => x !== k) : sel.length < HABIT_ACTIVE_MAX ? [...sel, k] : sel)
  const valid = sel.length >= HABIT_ACTIVE_MIN && sel.length <= HABIT_ACTIVE_MAX

  const save = async () => {
    setError('')
    setPending(true)
    try {
      await setActiveHabits(sel)
      await refreshPerjalanan(qc)
      onClose()
    } catch (e) {
      setError(msg(e))
    } finally {
      setPending(false)
    }
  }
  const add = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      const { habit } = await addHabit(label, pilar)
      setLabel('')
      if (sel.length < HABIT_ACTIVE_MAX) setActive([...sel, habit.key])
      await qc.invalidateQueries({ queryKey: ['perjalanan', 'kebiasaan'] })
    } catch (err) {
      setError(msg(err))
    }
  }
  const remove = async (key: string) => {
    setError('')
    try {
      await removeHabit(key)
      setActive(sel.filter((k) => k !== key))
      await refreshPerjalanan(qc)
    } catch (err) {
      setError(msg(err))
    }
  }

  return (
    <Sheet title="Atur kebiasaan" onClose={onClose}>
      <p className="help" style={{ marginTop: 0 }}>Pilih {HABIT_ACTIVE_MIN} sampai {HABIT_ACTIVE_MAX} kebiasaan untuk dicentang tiap hari. Pilih yang benar-benar ingin kamu jaga.</p>
      <ul className="hb-pick" aria-label="Pilihan kebiasaan">
        {(data?.all ?? []).map((h) => {
          const on = sel.includes(h.key)
          return (
            <li key={h.key}>
              <label className="hb-opt" data-off={!on && sel.length >= HABIT_ACTIVE_MAX ? '' : undefined}>
                <input type="checkbox" checked={on} disabled={!on && sel.length >= HABIT_ACTIVE_MAX} onChange={() => toggle(h.key)} />
                <span><b>{h.label}</b><small>{PILAR_LABEL[h.pilar]}{h.custom ? ' · buatanmu' : ''}</small></span>
              </label>
              {h.custom && (
                <button type="button" className="icon-btn plain" aria-label={`Hapus kebiasaan ${h.label}`} onClick={() => void remove(h.key)}><Trash2 size={18} aria-hidden="true" /></button>
              )}
            </li>
          )
        })}
      </ul>

      {(data?.customCount ?? 0) < HABIT_CUSTOM_MAX && (
        <form className="hb-add" onSubmit={add}>
          <label className="field">
            <span>Kebiasaan buatanmu</span>
            <input className="input" value={label} maxLength={60} onChange={(e) => setLabel(e.target.value)} placeholder="Misalnya: jalan pagi 10 menit" />
          </label>
          <div className="hb-add-row">
            <label className="hb-pilar">
              <span className="sr">Pilar</span>
              <select className="input" value={pilar} onChange={(e) => setPilar(e.target.value as Pilar)}>
                {PILAR.map((p) => <option key={p} value={p}>{PILAR_LABEL[p]}</option>)}
              </select>
            </label>
            <button type="submit" className="btn btn-ghost" disabled={label.trim().length < 3}>Tambah</button>
          </div>
        </form>
      )}

      <p className="error" role="alert">{error}</p>
      <button type="button" className="btn btn-primary btn-block" onClick={save} disabled={!valid || pending}>
        {pending ? 'Menyimpan…' : valid ? 'Simpan pilihan' : `Pilih ${HABIT_ACTIVE_MIN}–${HABIT_ACTIVE_MAX} kebiasaan`}
      </button>
    </Sheet>
  )
}

/**
 * Today's habits with a check each and the last 7 days as dots. Optimistic: the check flips at once, goes back if the
 * server says no. A missed day is greeted kindly (UX-PLAYBOOK §6.5), never as a red streak.
 */
export function HabitsCard({ habits, days, missedYesterday, onLight, onError }: {
  habits: HabitDay[]
  days: string[]
  missedYesterday: boolean
  onLight: (g: Garden) => void
  onError: (m: string) => void
}) {
  const [over, setOver] = useState<Record<string, boolean>>({})
  const [sheet, setSheet] = useState(false)
  const isOn = (h: HabitDay) => over[h.key] ?? h.today
  useEffect(() => setOver({}), [habits])

  const flip = async (h: HabitDay, el: HTMLElement) => {
    const done = !isOn(h)
    setOver((o) => ({ ...o, [h.key]: done }))
    if (done) sparkBurst(el, 10)
    try {
      onLight((await checkHabit(h.key, done)).garden)
    } catch (e) {
      setOver((o) => ({ ...o, [h.key]: !done }))
      onError(msg(e))
    }
  }
  const doneCount = habits.filter(isOn).length

  return (
    <section className="card pj-sec reveal" aria-labelledby="pj-hb-h">
      <div className="pj-sec-h">
        <h2 id="pj-hb-h">Kebiasaan hari ini</h2>
        <span className="pill pill-mint num">{doneCount}/{habits.length}</span>
        <button type="button" className="icon-btn plain" aria-label="Atur kebiasaan" onClick={() => setSheet(true)}><Settings2 size={18} aria-hidden="true" /></button>
      </div>
      {missedYesterday && <p className="pj-kind">Kemarin terlewat? Nggak apa-apa. Mulai lagi dari hari ini.</p>}
      {habits.length === 0 ? (
        <p className="muted">Belum ada kebiasaan yang dipilih. <button type="button" className="link-btn" onClick={() => setSheet(true)}>Pilih sekarang</button></p>
      ) : (
        <ul className="hb-list">
          {habits.map((h) => {
            const on = isOn(h)
            return (
              <li key={h.key} className="hb-row">
                <button type="button" role="checkbox" aria-checked={on} className="hb-check" onClick={(e) => void flip(h, e.currentTarget)}>
                  <span className="hb-box" aria-hidden="true">{on && <Check size={16} strokeWidth={3} />}</span>
                  <span className="hb-label">{h.label}</span>
                </button>
                <span className="hb-week" aria-label={`7 hari terakhir: ${h.week.map((w, i) => `${dayShort(days[i]!)} ${i === 6 ? (on ? 'sudah' : 'belum') : w ? 'sudah' : 'belum'}`).join(', ')}`} role="img">
                  {h.week.map((w, i) => <i key={i} className={(i === 6 ? on : w) ? 'on' : ''} />)}
                </span>
              </li>
            )
          })}
        </ul>
      )}
      {sheet && <HabitSheet onClose={() => setSheet(false)} />}
    </section>
  )
}
